// src/components/SignIn/hooks/useSignIn.js
import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStore } from '../../../stores';

export const useSignIn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authStore } = useStore(); // Removed uiStore to avoid global loading conflicts
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Local loading state

  // Check if redirected due to session expiration
  useEffect(() => {
    if (searchParams.get('session_expired') === 'true') {
      setError('Your session has expired. Please login again.');
    }
  }, [searchParams]);

  const resetErrors = useCallback(() => {
    setError('');
    setUsernameError(false);
    setPasswordError(false);
  }, []);

  const handleUsernameChange = useCallback((e) => {
    resetErrors();
    setUsername(e.target.value);
  }, [resetErrors]);

  const handlePasswordChange = useCallback((e) => {
    resetErrors();
    setPassword(e.target.value);
  }, [resetErrors]);

  const handleLogin = useCallback(async () => {
    resetErrors();

    // Validation
    if (!username.trim()) {
      setUsernameError(true);
      setError('Username is required.');
      return;
    }

    if (!password.trim()) {
      setPasswordError(true);
      setError('Password is required.');
      return;
    }

    setIsLoading(true);
    
    try {
        const loginSuccess = await authStore.PasswordLogin(username, password);
        if (loginSuccess) {
        // Check user role and redirect appropriately
        const isAdmin = authStore.user?.role === 'Admin' || authStore.user?.roles[0] === 1987;
        navigate(isAdmin ? '/AdminDashboard' : '/UserDashboard');
      } else {
        setError('Invalid username or password.');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        setError('Invalid credentials. Please check your username and password.');
      } else if (error.response?.status >= 500) {
        setError('Server error. Please try again later.');
      } else if (error.request) {
        setError('Network error. Please check your connection.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [username, password, authStore, navigate, resetErrors]);

  return {
    username,
    password,
    error,
    usernameError,
    passwordError,
    isLoading,
    handleUsernameChange,
    handlePasswordChange,
    handleLogin,
    resetErrors
  };
};